import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { useDropEngagement } from "../lib/dropEngagement";

/**
 * Heart-shaped like button for the drop page (`/live/:id`).
 *
 * - Public count is visible to everyone (viral vibe).
 * - Anonymous click routes to sign-in with a friendly toast.
 * - Optimistic toggle via the useDropEngagement hook.
 *
 * Variant:
 *   `overlay` — dark pill for the mobile immersive live overlay
 *   default   — light card style for the waiting-room hero area
 */
export default function LikeButton({ liveId, overlay = false }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const { likes, liked_by_me, loading, toggle } = useDropEngagement(liveId);

  const onClick = () => {
    if (!user) {
      toast("Sign in to react to this drop", { duration: 2500 });
      nav("/login");
      return;
    }
    toggle();
  };

  const base = "inline-flex items-center gap-1.5 font-mono-ds text-[11px] uppercase tracking-[0.20em] transition-all duration-200 select-none";
  const cls = overlay
    ? `${base} h-9 px-3 border ${liked_by_me ? "border-[#FF3B30] bg-[#FF3B30]/20 text-[#FF3B30]" : "border-white/25 bg-black/45 backdrop-blur text-white/85 hover:text-white"}`
    : `${base} h-11 px-4 border-2 ${liked_by_me ? "border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30]" : "border-[#161616] bg-white text-[#161616] hover:bg-[#F8F7F2]"}`;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cls}
      aria-pressed={liked_by_me}
      data-testid="drop-like-button"
    >
      <Heart filled={liked_by_me} />
      <span className="tabular-nums font-bold" data-testid="drop-like-count">
        {loading ? "·" : Number(likes).toLocaleString("en-IN")}
      </span>
      <span className="opacity-70">{liked_by_me ? "Liked" : "Like"}</span>
    </button>
  );
}

function Heart({ filled }) {
  return (
    <svg
      width="14" height="14" viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
      className={filled ? "animate-heart-beat" : ""}
      aria-hidden="true"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}
